import React from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import { Flow, FlowStatus } from "../../../../../stores/flowsStore";

interface ActiveFlowsListProps {
  flows: Flow[];
  loading: boolean;
  onEdit: (flow: Flow) => void;
  onDelete: (flowId: string) => void;
  onDuplicate: (flow: Flow) => void;
  onToggleStatus: (flow: Flow) => void;
}

const statusColors: Record<FlowStatus, "default" | "success" | "warning" | "error"> = {
  active: "success",
  inactive: "default",
  draft: "warning",
};

const statusLabels: Record<FlowStatus, string> = {
  active: "Active",
  inactive: "Inactive",
  draft: "Draft",
};

export const ActiveFlowsList: React.FC<ActiveFlowsListProps> = ({
  flows,
  loading,
  onEdit,
  onDelete,
  onDuplicate,
  onToggleStatus,
}) => {
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (flows.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography color="text.secondary">
          No flows created yet. Create your first automation flow!
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Conditions</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Created</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {flows.map((flow) => (
            <TableRow key={flow.identifier} hover>
              <TableCell>
                <Typography variant="body2" fontWeight={500}>
                  {flow.name}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                  {flow.description || "-"}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={`${flow.conditions.length} condition${flow.conditions.length !== 1 ? "s" : ""}`}
                  size="small"
                  variant="outlined"
                />
              </TableCell>
              <TableCell>
                <Chip
                  label={statusLabels[flow.status]}
                  size="small"
                  color={statusColors[flow.status]}
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {flow.created_at
                    ? new Date(flow.created_at).toLocaleDateString()
                    : "-"}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                  <Tooltip title={flow.status === "active" ? "Deactivate" : "Activate"}>
                    <IconButton
                      size="small"
                      onClick={() => onToggleStatus(flow)}
                      color={flow.status === "active" ? "warning" : "success"}
                    >
                      {flow.status === "active" ? (
                        <PauseIcon fontSize="small" />
                      ) : (
                        <PlayArrowIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => onEdit(flow)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Duplicate">
                    <IconButton size="small" onClick={() => onDuplicate(flow)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={() => onDelete(flow.identifier)}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
